// .pen UGuBc 页面-资源包-添加文件 — 确认时把选中文件夹递归(BFS)展开为其内全部文件。
//
// 裁决依据(spec 2026-06-07 §2):pack item 只能是 file —— 后端 CanAccessFile
//(server/internal/pack/save_inline.go:163)对 pack item 按精确 file_info_id 匹配,
// folder item 不授予买家子文件访问权;转存/下载也都拒 folder。展开在前端做,后端零改动。
//
// 纯逻辑模块:fetchChildren 注入(组件传 fetchFilePickPage,测试传 mock),不依赖 react。

export type ExpandLimits = {
  maxItems: number
  maxDepth: number
  maxRequests: number
}

// maxItems 对齐后端 items 上限(create-pack §5 Case 14);depth/requests 是防环/防滥用护栏
export const EXPAND_LIMITS: ExpandLimits = { maxItems: 1000, maxDepth: 20, maxRequests: 200 }

export type ExpandErrorCode = 'TOO_MANY_ITEMS' | 'TOO_DEEP' | 'TOO_MANY_REQUESTS'

export class ExpandError extends Error {
  readonly code: ExpandErrorCode

  constructor(code: ExpandErrorCode, message: string) {
    super(message)
    this.name = 'ExpandError'
    this.code = code
  }
}

type Expandable = { id: string; type: string }

/**
 * 把选择集(文件+文件夹)展开为纯文件数组:
 * 直选文件保序在前;文件夹按 BFS 逐层拉子项,子文件追加、子文件夹入队;按 id 去重。
 */
export async function expandSelection<T extends Expandable>(
  selected: readonly T[],
  fetchChildren: (parentId: string) => Promise<T[]>,
  limits: ExpandLimits = EXPAND_LIMITS,
): Promise<T[]> {
  const out = new Map<string, T>()
  const queue: { id: string; depth: number }[] = []
  for (const item of selected) {
    if (item.type === 'folder') {
      queue.push({ id: item.id, depth: 1 })
    } else {
      out.set(item.id, item)
    }
  }
  if (out.size > limits.maxItems) {
    throw new ExpandError('TOO_MANY_ITEMS', `文件过多(上限 ${limits.maxItems}),请减少选择`)
  }

  let requests = 0
  let head = queue.shift()
  while (head !== undefined) {
    if (head.depth > limits.maxDepth) {
      throw new ExpandError('TOO_DEEP', `文件夹嵌套超过 ${limits.maxDepth} 层,无法展开`)
    }
    requests += 1
    if (requests > limits.maxRequests) {
      throw new ExpandError('TOO_MANY_REQUESTS', '文件夹内容过多,请减少选择')
    }
    const children = await fetchChildren(head.id)
    for (const child of children) {
      if (child.type === 'folder') {
        queue.push({ id: child.id, depth: head.depth + 1 })
      } else {
        out.set(child.id, child)
        if (out.size > limits.maxItems) {
          throw new ExpandError('TOO_MANY_ITEMS', `文件过多(上限 ${limits.maxItems}),请减少选择`)
        }
      }
    }
    head = queue.shift()
  }
  return Array.from(out.values())
}
